<template>
  <v-app-bar :color="toolbarColor" fixed class="px-2">
    <template v-slot:prepend>
      <v-app-bar-nav-icon
        @click.stop="navClicked"
        class="color-white"
      ></v-app-bar-nav-icon>
    </template>
    <v-app-bar-title class="text-h6 font-weight-medium color-white">{{
      title
    }}</v-app-bar-title>

    <v-spacer />
    <template v-if="responsive && showMobileOptions">
      <v-btn @click="mobileSearchClicked">
        <v-icon>fa-solid fa-magnifying-glass</v-icon>
      </v-btn>
      <v-btn @click="mobileSortClicked">
        <v-icon>{{
          sorticon ? "fas fa-sort-alpha-up" : "fas fa-sort-alpha-down"
        }}</v-icon>
      </v-btn>
    </template>
    <!-- Only for broker admins and impersonation allow to invite from tool bar-->
    <v-btn
      v-if="allowInviteAgents"
      variant="flat"
      class="bg-white text-primary text-none"
      prepend-icon="fa-solid fa-plus"
      rounded="xl"
      elevation="2"
      @click="inviteAgents"
    >
      Invite Agents
    </v-btn>
    <agent-invite-dialog
      :dialog="agentInviteDialog"
      @agentsInvited="agentsInvited"
      @close="agentIniviteClosed"
      @update:dialog="agentInviteDialog = $event"
    />

    <div class="notification-menu-wrapper">
      <v-menu
        v-if="showNotificationMenu"
        v-bind="notificationMenu"
        :close-on-click="true"
        :close-on-content-click="false"
        :nudge-left="328"
        :nudge-bottom="5"
        offset-y
        content-class="notification-menu"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            icon="fa-solid fa-envelope"
            color="white"
            class="mx-2 text-white"
          >
          </v-btn>
        </template>
        <my-notifications-quick-list />
      </v-menu>
    </div>
    <v-menu
      v-model="navMenu"
      :close-on-click="true"
      :close-on-content-click="false"
      offset-y
    >
      <template v-slot:activator="{ props: activatorProps }">
        <template v-if="responsive">
          <v-btn
            color="white"
            prepend-icon="fa-solid fa-user"
            v-bind="activatorProps"
          >
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            v-bind="activatorProps"
            color="white"
            variant="text"
            prepend-icon="fa-solid fa-user"
            rounded
          >
            <span class="text-none">{{ userDisplayName }}</span>
          </v-btn>
        </template>
      </template>
      <v-list>
        <v-list-item>
          <v-row no-gutters>
            <v-col align-self="center">
              <v-list-item-title style="cursor: default">
                Stay Logged In
              </v-list-item-title>
            </v-col>
            <v-col cols="">
              <v-list-item-action class="switch-align-right">
                <v-switch color="primary" v-model="autoAuth" hide-details />
              </v-list-item-action>
            </v-col>
          </v-row>
        </v-list-item>
        <v-list-item v-if="showEmailSettings" @click="emailSettings">
          <v-list-item-title>Email Settings</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="showProfileOption" @click="profileClicked">
          <v-list-item-title>Profile</v-list-item-title>
        </v-list-item>
        <v-list-item @click="updatePasswordClicked()">
          <v-list-item-title>Update Password</v-list-item-title>
        </v-list-item>
        <v-list-item @click="logoutClicked()">
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import MyNotificationsQuickList from "@/components/console/my-notifications/MyNotificationsQuickList";
import useFormatters from "@/mixins/formatters";
import { defineAsyncComponent } from "vue";
import { useEventBus } from "@/event-bus/bus";
import $const from "@/utils/constants";

export default {
  name: "console-toolbar",
  components: {
    MyNotificationsQuickList,
    AgentInviteDialog: defineAsyncComponent(
      () => import("@/components/console/agents/AgentInviteDialog"),
    ),
  },
  data() {
    return {
      agentInviteDialog: false,
      navMenu: false,
      notificationMenu: false,
      autoAuth: false,
    };
  },
  watch: {
    $route() {
      this.notificationMenu = false;
    },
    autoAuth() {
      this.setSessionAutoRefresh(this.autoAuth);
    },
    sessionAutoRefresh() {
      this.autoAuth = this.sessionAutoRefresh;
    },
  },
  computed: {
    ...mapGetters("user", [
      "me",
      "agent",
      "myBrokerage",
      "isAdmin",
      "isBrokerAdmin",
      "isLoanOfficer",
      "isAgent",
      "isAgentOnly",
      "responsive",
      "mobileBrokerSort",
      "mobileAgentSort",
    ]),
    ...mapGetters("auth", ["impBrokerage", "sessionAutoRefresh"]),
    title() {
      console.log(`route.name: ${this.$route.name}`);
      console.log(`route.meta: ${JSON.stringify(this.$route.meta)}`);
      console.log(`route.meta.title: ${this.$route.meta.title}`);

      // need to migrate all below titles to each route
      if (this.$route.meta?.title) {
        return this.$route.meta.title;
      }

      // start migrating this out of this switch and to the individual routes
      switch (this.$route.name) {
        case "brokerages":
          return "Brokers";
        case "agents":
        case "view-agent":
          return "Agents";
        case "lenders":
          return "Lenders";
        case "opportunities":
        case "view-opportunity":
          return "Opportunities";
        case "dnc-list":
          return "DNC List";
        case "user-management":
        case "user-edit":
        case "user-create":
          return "User Management";
        case "supplier-management":
        case "supplier-create":
        case "view-supplier":
          return "Supplier Management";
        case "qa-review":
        case "qa-review-opportunity":
          return "QA Review";
        case "announcements":
          return "Announcements";
        case "agent-assignments":
          return "Agent Assignments";
        case "simulator":
          return "Simulator";
        case "content-search":
          return "Content Search";
        case "console":
          return "Overview";
        case "marketplace":
          return "Marketplace";
        case "my-agents":
        case "view-my-agent":
          return "My Agents";
        case "my-admins":
        case "view-my-admin":
          return "My Admins";
        case "my-referrals":
        case "view-my-referral":
          return "My Referrals";
        case "my-notifications":
          return "My Notifications";
        case "my-announcements":
          return "My Announcements";
        case "my-metrics":
          return "My Metrics";
        case "my-leads":
        case "view-my-lead":
          return "My Leads";
        case "twilio-tasks":
          return "Twilio Tasks";
        case "my-loan-officers":
          return "My Loan Officers";
        case "lender-referrals":
        case "view-lender-referral":
          return "My Referrals";
        case "settings-email":
          return "Email Settings";
        case "agent-followups":
        case "view-agent-referral":
          return "Follow-ups";
        case "agent-funnel":
        case "agent-funnel-buyer":
        case "agent-funnel-seller":
        case "agent-funnel-renter":
          return "My Funnel";
        case "agent-profile":
          return "Profile";
      }

      return "";
    },
    userDisplayName() {
      // show brokerage name if available
      if (this.myBrokerage && this.myBrokerage.name) {
        console.log("THis name?");
        console.log(this.myBrokerage.name);
        return this.myBrokerage.name;
      }

      // show agent name if agent
      if (this.isAgent && this.agent) {
        const agentName = this.agentName(this.agent);
        if (agentName !== "") {
          return agentName;
        }
      }

      // show first and last name if available
      if (this.me.firstName && this.me.lastName) {
        return `${this.me.firstName} ${this.me.lastName}`;
      }

      // show firstname if available
      if (this.me.firstName) {
        return this.me.firstName;
      }

      // show email if available
      if (this.me.email) {
        return this.me.email;
      }

      // catch-all - just show icon?
      return "";
    },
    toolbarColor() {
      return "primary";
    },
    sorticon() {
      switch (this.$route.path) {
        case "/console":
          if (this.isAdmin) {
            return this.mobileBrokerSort;
          } else if (this.isBrokerAdmin) {
            return this.mobileAgentSort;
          }
          break;

        case "/console/my-agents":
          return this.mobileAgentSort;

        case "/console/brokerages":
          return this.mobileBrokerSort;
      }

      return this.mobileAgentSort;
    },
    showMobileOptions() {
      switch (this.$route.name) {
        case "qa-review":
        case "qa-review-opportunity":
          return false;
      }

      return true;
    },
    showNotificationMenu() {
      switch (this.$route.name) {
        case "console":
          return !this.isLoanOfficer;
        case "my-agents":
        case "view-my-agent":
        case "my-admins":
        case "view-my-admin":
        case "my-referrals":
        case "view-my-referral":
        case "my-notifications":
          return true;
        default:
          return false;
      }
    },
    allowInviteAgents() {
      return this.isBrokerAdmin || this.impBrokerage !== null;
    },
    showEmailSettings() {
      return this.myBrokerage;
    },
    showProfileOption() {
      return this.isAgentOnly;
    },
  },
  methods: {
    ...useFormatters(),
    ...mapMutations("user", [
      "setDrawer",
      "toggleDrawer",
      "setResponsive",
      "toggleMobileBrokerSort",
      "toggleMobileAgentSort",
      "toggleMobileBrokerSearch",
      "toggleMobileAgentSearch",
    ]),
    ...mapMutations("auth", ["setSessionAutoRefresh"]),
    inviteAgents() {
      console.log("invite agents from toolbar");
      this.agentInviteDialog = true;
    },
    agentsInvited(error) {
      if (!error) {
        console.log("Agents invited");
        this.agentInviteDialog = false;
        // If we are on the my-agents view, trigger reload of the data table
        switch (this.$route.path) {
          case "/console/my-agents": {
            const _eventBus = useEventBus();
            _eventBus.$emit($const.EVENT_BUS.RELOAD_INVITED_AGENTS);
            break;
          }
        }
      }
    },
    agentIniviteClosed() {
      this.agentInviteDialog = false;
    },
    updatePasswordClicked() {
      this.navMenu = false;
      this.$emit("updatePassword");
    },
    logoutClicked() {
      this.navMenu = false;
      this.$emit("logoutClicked");
    },
    emailSettings() {
      console.log("email settings....");
      this.navMenu = false;
      this.$router.push({ name: "settings-email" });
    },
    navClicked() {
      this.setDrawer(!this.$store.state.user.drawer);
    },
    profileClicked() {
      this.$router.push({ name: "agent-profile" });
    },
    onResponsiveInverted() {
      if (window.innerWidth < 768) {
        this.setResponsive(true);
      } else {
        this.setResponsive(false);
      }
    },
    mobileSortClicked() {
      switch (this.$route.path) {
        case "/console":
          if (this.isAdmin) {
            this.toggleMobileBrokerSort();
          } else if (this.isBrokerAdmin) {
            this.toggleMobileAgentSort();
          }
          break;

        case "/console/my-agents":
          this.toggleMobileAgentSort();
          break;
        case "/console/brokerages":
          this.toggleMobileBrokerSort();
          break;
      }
    },
    mobileSearchClicked() {
      switch (this.$route.path) {
        case "/console":
          if (this.isAdmin) {
            this.toggleMobileBrokerSearch();
          } else if (this.isBrokerAdmin) {
            this.toggleMobileAgentSearch();
          }
          break;

        case "/console/my-agents":
          this.toggleMobileAgentSearch();
          break;
        case "/console/brokerages":
          this.toggleMobileBrokerSearch();
          break;
      }
    },
  },
  mounted() {
    this.autoAuth = this.sessionAutoRefresh;

    this.onResponsiveInverted();
    window.addEventListener("resize", this.onResponsiveInverted);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResponsiveInverted);
  },
  created: function () {},
};
</script>

<style scoped>
.v-toolbar :deep(.v-btn .v-icon) {
  font-size: 18px;
}

.stay-logged-in-switch {
  justify-content: right;
  min-width: 38px !important;
}
.stay-logged-in-switch :deep(.v-input) {
  width: 38px !important;
  flex-grow: unset !important;
}
.stay-logged-in-switch :deep(.v-input--selection-controls__input) {
  margin-right: 0 !important;
}
.notification-menu {
  border-radius: 10px;
  contain: initial;
  overflow: visible;
}
.notification-menu::before {
  border-bottom: 13px solid rgb(249, 249, 249);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  content: "";
  filter: drop-shadow(0 3px 1px rgba(0, 0, 0, 0.2))
    drop-shadow(0 2px 2px rgba(0, 0, 0, 0.14))
    drop-shadow(0 1px 5px rgba(0, 0, 0, 0.12));
  height: 10px;
  position: absolute;
  right: 9.5%;
  top: 0;
  transform: translateY(-100%);
  width: 13px;
}

.color-white {
  color: white;
}

.switch-align-right {
  justify-content: right !important;
}

.v-switch :deep(.v-selection-control) {
  min-height: unset !important;
}
</style>
